import type { EmailCommConfigPayload } from '@/api/useJobStageActions/useCreateJobStageAction';
import { convertPlainTextToHtml } from '@/utils/textarea-parser';
import {
  attachEditorItemsToCommConfig,
  commConfigToEmailComposition,
  emailCompositionToCommConfig,
  isVacuousEmailCommConfig,
  patchEmailComposition,
} from './emailUtils';
import { mapTemplateAttachmentsToEditorItems } from './templateApply';
import { initialInterviewCommunicationFormState } from '../state';
import type {
  InterviewCommunicationFormState,
  InterviewCommunicationPayload,
} from '../types';

/**
 * Calendar-invite bodies are stored as plain text by the `feHtmlToText`
 * save path (see `mapCommunication.stripHtmlWhenUnformatted`) — the body
 * comes back as `"Hi,\n\nI wanted to..."` instead of `<p>` blocks. The
 * rich-text editor renders raw `\n` as collapsed whitespace, which made
 * multi-line invites appear as a single line on edit. Re-paragraph plain
 * bodies before composing.
 *
 * Mirrors legacy `KInterviewScheduler/CalendarInvites/Container.tsx#148`.
 *
 * Skips bodies that already contain HTML so we don't double-wrap.
 */
function restoreCalendarBodyHtml(
  cfg: EmailCommConfigPayload
): EmailCommConfigPayload {
  const body = (cfg as { body?: unknown }).body;
  if (typeof body !== 'string' || body.length === 0) return cfg;
  // Tighter than `body.includes('<')` — avoids false positives on plain-text
  // angle brackets like `Send to <user@example.com>`.
  if (/<\/?[a-z][a-z0-9-]*(\s[^>]*)?\/?>/i.test(body)) return cfg;
  return { ...cfg, body: convertPlainTextToHtml(body) };
}

/**
 * Serializes form state into `payloadRef.communication` / create-interview body fragment.
 */
export function toInterviewCommunicationPayload(
  state: InterviewCommunicationFormState,
  options?: { currentUserId?: number }
): InterviewCommunicationPayload {
  const send_calendar_invite_to_candidate = true;
  const send_calendar_invite_to_interviewer = true;
  const send_email_to_candidate = state.candidateEmailEnabled;
  const send_email_to_interviewer = state.interviewerEmailEnabled;
  const send_ics_to_candidate = state.candidateIcsEnabled;
  const send_ics_to_interviewer = state.interviewerIcsEnabled;

  const candidate_email_comm_config = state.candidateEmailEnabled
    ? attachEditorItemsToCommConfig(
        emailCompositionToCommConfig(state.candidateEmail, options),
        state.candidateEmailAttachments
      )
    : undefined;

  const interviewer_email_comm_config = state.interviewerEmailEnabled
    ? attachEditorItemsToCommConfig(
        emailCompositionToCommConfig(state.interviewerEmail, options),
        state.interviewerEmailAttachments
      )
    : undefined;

  // Slot-selection email is always on (toggle UI commented out); always emit config + flag.
  // const candidate_self_schedule_email_comm_config = state.selfScheduleEnabled
  //   ? attachEditorItemsToCommConfig(
  //       emailCompositionToCommConfig(state.selfSchedule, options),
  //       state.selfScheduleAttachments
  //     )
  //   : undefined;
  const candidate_self_schedule_email_comm_config =
    attachEditorItemsToCommConfig(
      emailCompositionToCommConfig(state.selfSchedule, options),
      state.selfScheduleAttachments
    );

  return {
    activeSubTab: state.activeSubTab,
    send_calendar_invite_to_candidate,
    send_calendar_invite_to_interviewer,
    send_email_to_candidate,
    send_email_to_interviewer,
    send_ics_to_candidate,
    send_ics_to_interviewer,
    calendar_invite_candidate: emailCompositionToCommConfig(
      state.candidateCalendar
    ),
    calendar_invite_interviewer: emailCompositionToCommConfig(
      state.interviewerCalendar
    ),
    candidate_email_comm_config,
    interviewer_email_comm_config,
    candidate_self_schedule_email_comm_config,
    // self_schedule_section_enabled: state.selfScheduleEnabled,
    self_schedule_section_enabled: true,
  };
}

export function getInitialInterviewCommunicationPayload(): InterviewCommunicationPayload {
  return toInterviewCommunicationPayload(
    initialInterviewCommunicationFormState
  );
}

export type HydrateCommunicationOptions = {
  /** Edit load: prefill body from API but leave the template dropdown unset. */
  stripCalendarTemplates?: boolean;
};

/** Maps `payloadRef.communication` (from GET or defaults) into Communication step reducer state. */
export function hydrateCommunicationStateFromPayload(
  payload: Partial<InterviewCommunicationPayload> | undefined,
  base: InterviewCommunicationFormState,
  options?: HydrateCommunicationOptions
): InterviewCommunicationFormState {
  if (!payload) {
    return base;
  }
  const mapCalendar = (
    cfg: EmailCommConfigPayload | null | undefined,
    fallback: InterviewCommunicationFormState['candidateCalendar']
  ) => {
    if (!cfg || isVacuousEmailCommConfig(cfg)) {
      return fallback;
    }
    const composed = commConfigToEmailComposition(restoreCalendarBodyHtml(cfg));
    return options?.stripCalendarTemplates
      ? patchEmailComposition(composed, { template: null })
      : composed;
  };

  // Legacy activities (created before these sections existed) come back
  // as `{}` from the BE comm_configs partial. `{}` is truthy, so the
  // old `payload.x ? compose(x) : base` ternary fell into `compose({})`
  // → `from: ''`. Mirror `mapCalendar`: treat a vacuous config like an
  // absent one and keep `base` (which carries the organizer sentinel),
  // so blank sections default From to the organizer instead of empty.
  const mapEmail = (
    cfg: EmailCommConfigPayload | null | undefined,
    fallback: InterviewCommunicationFormState['candidateEmail']
  ) =>
    !cfg || isVacuousEmailCommConfig(cfg)
      ? fallback
      : commConfigToEmailComposition(cfg);

  const mapEmailAttachments = (
    cfg: EmailCommConfigPayload | null | undefined,
    fallback: InterviewCommunicationFormState['candidateEmailAttachments']
  ) =>
    !cfg || isVacuousEmailCommConfig(cfg)
      ? fallback
      : mapTemplateAttachmentsToEditorItems(cfg.attachments ?? []);

  return {
    ...base,
    activeSubTab: payload.activeSubTab ?? base.activeSubTab,
    /** Only hydrate calendar blocks when GET returned real subject/body (defaults are vacuous). */
    candidateCalendar: mapCalendar(
      payload.calendar_invite_candidate,
      base.candidateCalendar
    ),
    interviewerCalendar: mapCalendar(
      payload.calendar_invite_interviewer,
      base.interviewerCalendar
    ),
    candidateEmailEnabled:
      payload.send_email_to_candidate ?? base.candidateEmailEnabled,
    candidateEmail: mapEmail(
      payload.candidate_email_comm_config,
      base.candidateEmail
    ),
    candidateEmailAttachments: mapEmailAttachments(
      payload.candidate_email_comm_config,
      base.candidateEmailAttachments
    ),
    interviewerEmailEnabled:
      payload.send_email_to_interviewer ?? base.interviewerEmailEnabled,
    interviewerEmail: mapEmail(
      payload.interviewer_email_comm_config,
      base.interviewerEmail
    ),
    interviewerEmailAttachments: mapEmailAttachments(
      payload.interviewer_email_comm_config,
      base.interviewerEmailAttachments
    ),
    // Toggle hidden; treat slot-selection email as always on regardless of stored flag.
    // selfScheduleEnabled:
    //   payload.self_schedule_section_enabled ?? base.selfScheduleEnabled,
    selfScheduleEnabled: true,
    selfSchedule: mapEmail(
      payload.candidate_self_schedule_email_comm_config,
      base.selfSchedule
    ),
    selfScheduleAttachments: mapEmailAttachments(
      payload.candidate_self_schedule_email_comm_config,
      base.selfScheduleAttachments
    ),
    candidateIcsEnabled:
      payload.send_ics_to_candidate ?? base.candidateIcsEnabled,
    interviewerIcsEnabled:
      payload.send_ics_to_interviewer ?? base.interviewerIcsEnabled,
  };
}
